import React from 'react';
import { ComplianceCheck } from '../../utils/types';
import { SECTION_META } from '../../designTokens';
import {
  CONFIRM_GROUP_NOTE,
  FIX_GROUP_LABEL,
  FIX_GROUP_ORDER,
  FixGroup,
  crosscheckTag,
  fixBadge,
  issueCardId,
} from './fixGroups';
import { ConfirmedChip, DisputedChip } from './crosscheck/CrosscheckParts';

interface FixIndexProps {
  groups: Record<FixGroup, ComplianceCheck[]>;
}

/**
 * Scrolls to the card and moves focus onto it. The report lives under a hash
 * route (`#/scan/:id`), so the default jump to `#issue-…` would change the route:
 * always prevent it. A card inside the collapsed Confirm group opens it first.
 */
function jumpToCard(event: React.MouseEvent<HTMLAnchorElement>, id: string) {
  event.preventDefault();
  const card = document.getElementById(id);
  if (!card) return;
  const details = card.closest('details');
  if (details && !details.open) details.open = true;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  card.focus({ preventScroll: true });
}

/**
 * The fix index at the top of "What to fix" (#1660): one link row per card,
 * grouped Fix these / Recommended / Confirm it applies to you. Rows are native
 * anchors, so Tab reaches each one and Enter fires the click handler.
 */
export default function FixIndex({ groups }: FixIndexProps) {
  return (
    <nav className="fix-index no-print" aria-label="Fix index">
      {FIX_GROUP_ORDER.filter((g) => groups[g].length > 0).map((group) => (
        <div key={group} className="fix-index__group" data-fix-group={group}>
          <div className="fix-index__group-heading">
            <span className="fix-index__group-label">
              {FIX_GROUP_LABEL[group]} · {groups[group].length}
            </span>
            {group === 'confirm' && <span className="fix-index__group-note">{CONFIRM_GROUP_NOTE}</span>}
          </div>
          <ul className="fix-index__rows">
            {groups[group].map((check) => {
              const id = issueCardId(check);
              const badge = fixBadge(check, group);
              const tag = crosscheckTag(check.adjudication);
              return (
                <li key={check.id}>
                  <a href={`#${id}`} className="fix-index__row" onClick={(e) => jumpToCard(e, id)}>
                    <span className={`severity-badge severity-badge--${badge.modifier} fix-index__badge`}>
                      {badge.text}
                    </span>
                    <span className="fix-index__name">{check.name}</span>
                    <span className="fix-index__area">
                      {check.section ? (SECTION_META[check.section]?.label ?? check.section) : ''}
                    </span>
                    <span className="fix-index__tag">
                      {tag === 'Confirmed' && <ConfirmedChip />}
                      {tag === 'Disputed' && <DisputedChip />}
                    </span>
                    <span className="fix-index__chevron" aria-hidden="true">›</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
